






























































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { isVideoFile } from "@/lib/fileHelpers";
import { ProcessedContentFiles } from "@/types";
import BaseDuration from "@/components/BaseDuration";
import BaseIcon from "@/components/BaseIcon";
import FullScreen from "@/components/FullScreen";
import FullscreenArrow from "@/components/FullscreenArrow";
import GalleryTotalItems from "@/components/GalleryTotalItems";
import ContentFeedGalleryItem from "../ContentFeedGalleryItem";

interface FileListInfo {
  accessed: boolean;
  file: ContentFile | null;
  id: string;
}

interface AllFiles {
  audio?: ProcessedContentFiles;
  media: ProcessedContentFiles;
  documents: ProcessedContentFiles;
}

export default defineComponent({
  name: "ContentFeedGallery",
  components: {
    FullscreenArrow,
    BaseDuration,
    FullScreen,
    ContentFeedGalleryItem,
    BaseIcon,
    GalleryTotalItems,
  },
  props: {
    files: { type: Object as () => AllFiles, required: true },
    feedId: { type: String, required: true },
    blur: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const currentItemSetter = ref<(count: number) => void>();
    const currentItemIndex = ref(0);
    const fileClicked = ref(false);
    const videoPlaying = ref(false);
    const currentVideoTime = ref(0);

    const accessedMediaFiles = computed(() => props.files.media.accessed);
    const accessedDocumentFiles = computed(() => props.files.documents.accessed);
    const lockedMediaFiles = computed(() => props.files.media.locked);
    const lockedAudioFiles = computed(() => props.files.audio?.locked ?? []);
    const lockedDocumentFiles = computed(() => props.files.documents.locked);
    const allFiles = computed(() => {
      return accessedMediaFiles.value.concat(
        accessedDocumentFiles.value,
        lockedMediaFiles.value,
        lockedDocumentFiles.value,
        lockedAudioFiles.value
      );
    });

    const accessedFiles = computed(() => accessedMediaFiles.value.concat(accessedDocumentFiles.value));

    const allLockedFiles = computed(() => {
      return lockedMediaFiles.value.concat(lockedDocumentFiles.value, lockedAudioFiles.value);
    });
    const listLength = computed(() => {
      return (
        accessedMediaFiles.value.length +
        accessedDocumentFiles.value.length +
        lockedMediaFiles.value.length +
        lockedDocumentFiles.value.length +
        lockedAudioFiles.value.length
      );
    });
    const lockFullScreen = ref<boolean>(false);

    const renderFileList = computed<FileListInfo[]>(() => {
      const lockedFiles = allLockedFiles.value.map(file => ({ accessed: false, file, id: file.id } as FileListInfo));
      return accessedMediaFiles.value
        .concat(accessedDocumentFiles.value)
        .map(file => ({ accessed: true, file, id: file.id } as FileListInfo))
        .concat(lockedFiles);
    });

    const galleryItemStyle = computed(() => {
      const file = accessedMediaFiles.value.length ? accessedMediaFiles.value[0] : lockedMediaFiles.value[0];
      if (file && file.width > 0 && file.height > 0) {
        return {
          "padding-bottom": `${Math.min(Math.ceil((file.height / file.width) * 100), 150)}%`,
        };
      }
      return { "padding-bottom": "57%" };
    });

    const scalable = computed(
      () =>
        accessedMediaFiles.value[currentItemIndex.value] &&
        !isVideoFile(accessedMediaFiles.value[currentItemIndex.value])
    );

    const onChangeIndex = (newIndex: number) => {
      currentItemIndex.value = newIndex;
    };

    const onFileClick = () => {
      fileClicked.value = true;
    };

    const onItemClick = (currentItem: number, isFullScreen: boolean, cb: () => void) => {
      if (!accessedFiles.value[currentItem]) {
        emit("unlock");
        return;
      }
      if (!isFullScreen) {
        cb();
        return;
      }
      onFileClick();
    };

    const resetFile = () => {
      fileClicked.value = false;
      currentVideoTime.value = 0;
    };
    watch(allFiles, (files: ContentFile[], old: ContentFile[]) => {
      if (isEqual(files, old)) return;
      currentItemSetter.value && currentItemSetter.value(0);
    });

    return {
      accessedMediaFiles,
      lockedMediaFiles,
      galleryItemStyle,
      fileClicked,
      listLength,
      currentItemSetter,
      scalable,
      onChangeIndex,
      resetFile,
      renderFileList,
      lockFullScreen,
      allLockedFiles,
      allFiles,
      videoPlaying,
      isVideoFile,
      onItemClick,
      onFileClick,
      currentVideoTime,
    };
  },
});
